import { ApolloQueryResult } from '@apollo/client';
import defaultTo from 'lodash/defaultTo';
import toNumber from 'lodash/toNumber';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import { Maybe } from '@css-xp/graphtypes';
import { EntitlementDetails, Promotion } from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { logTimeStamp } from '@atlassian/temp-metal';
import { updateEntitlementPlan, updateEntitlementPromotions } from '@atlassian/temp-proxies';
import {
  PatchOperation,
  PatchRequestOperation,
  PromotionInstance,
  UpdateOrderRequest,
  CustomOrderEntitlement,
} from '@atlassian/cssxp-typescommon';
import { isOfferingAtNotice, constructCustomOrderRequest } from '@atlassian/cssxp-utils-entitlement';
import { ENABLE_REMOVE_PROMOTION_API, ENABLE_OMS_SERVICE_V2 } from '@atlassian/cssxp-utils-generic';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { useApprovalDeskPermissions } from '@css-xp/apd-common';
import { TaskDetailAction, useCancelRequest, useTaskList } from '@css-xp/apd-task-manager';
import {
  ErrorComponents,
  InflightInvoiceBanner,
  InfoPanel,
  NotificationTypes,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
  withApollo,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import { ActivePromotionSection } from '../../../components/ActivePromotionSection';
import { HeaderIcon } from '../../../components/HeaderIcon';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';

type ActivePromotion = Promotion & { isRemoved: boolean };

interface Props {
  entitlement: EntitlementDetails;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  transactionAccountContext: TransactionAccountContextType;
}

type SelectedPromotion = {
  promotionId: string;
  index: string;
};

interface FormData {
  supportTicketLink?: string | null;
  handNote?: string | null;
}

const constructRequestData = (
  entitlement: EntitlementDetails,
  {
    activeEntitlementPromotionsToRemove,
    scheduledPromotionsToRemove,
    commonSchedulesAndActivePromotions,
  }: {
    activeEntitlementPromotionsToRemove: SelectedPromotion[];
    scheduledPromotionsToRemove: SelectedPromotion[];
    commonSchedulesAndActivePromotions: SelectedPromotion[];
  },
  patchRemovePromotionEnabled: boolean,
  formData: FormData,
  isOmsServiceV2Enabled: boolean
  // eslint-disable-next-line max-params
) => {
  const requestData: UpdateOrderRequest = {
    metadata: {
      [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData.supportTicketLink ?? null,
      [OMSMetadata.HAND_NOTE]: formData.handNote ?? null,
    },
    entitlementId: entitlement.id,
    isPromotionStackingAllowed: true,
    productId: entitlement.productId || '', //@ts-ignore
    chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
  };
  let operations;
  const removePromotionInstance: PromotionInstance[] = [];

  if (patchRemovePromotionEnabled) {
    const activePromotionOperations: PatchRequestOperation[] = [];
    const scheduledPromotionOperations: PatchRequestOperation[] = [];

    activeEntitlementPromotionsToRemove.forEach((promo) => {
      activePromotionOperations.push({
        op: PatchOperation.REMOVE,
        path: `/subscription/promotionInstances/${promo.index}`,
      });
    });

    scheduledPromotionsToRemove.forEach((promo) => {
      scheduledPromotionOperations.push({
        op: PatchOperation.REMOVE,
        path: `/subscription/scheduledChanges/promotionInstances/${promo.index}`,
      });
    });

    commonSchedulesAndActivePromotions.forEach((promo) => {
      activePromotionOperations.push({
        op: PatchOperation.REMOVE,
        path: `/subscription/promotionInstances/${promo.index}`,
      });
    });
    operations = [...activePromotionOperations, ...scheduledPromotionOperations];
    requestData.operations = operations;
  } else {
    const promotionsToRemove = [...activeEntitlementPromotionsToRemove, ...scheduledPromotionsToRemove];

    promotionsToRemove?.forEach((promo) => {
      const promotionInstance: PromotionInstance = {
        promotionDefinition: {
          promotionId: promo.promotionId,
        },
      };
      removePromotionInstance.push(promotionInstance);
      requestData.removePromotions = removePromotionInstance;
    });
  }

  if (isOmsServiceV2Enabled) {
    const entitlementsList: CustomOrderEntitlement[] = [
      {
        entitlementId: entitlement.id,
        productId: entitlement.productId as string,
        removePromotions: removePromotionInstance,
        operations: operations,
      },
    ];

    const constructV2Request = {
      metadata: {
        [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData.supportTicketLink ?? null,
        [OMSMetadata.HAND_NOTE]: formData.handNote ?? null,
      },
      orderType: OrderItemType.PATCH_ENTITLEMENT_DETAILS_ORDER,
      billingFrequency: entitlement?.billingFrequency as string,
      entitlements: entitlementsList,
      transactionAccountId: entitlement?.transactionAccountId as string,
    };
    return constructCustomOrderRequest(constructV2Request);
  }
  return requestData;
};

export const EntitlementRemovePromotionPane = withApollo(({ entitlement, refetch }: Props) => {
  const { isApprovalDeskEnabled } = useApprovalDeskPermissions();
  const {
    cancelApprovalRequestError,
    cancelApprovalRequestProcessing,
    submitRequestAfterApprovalCancelation,
  } = useCancelRequest({
    businessKey: entitlement.entitlementNumber,
  });
  const { hasOpenApprovalRequest, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlement.entitlementNumber,
    skip: !entitlement.entitlementNumber || !isApprovalDeskEnabled,
  });

  const startTime = new Date().getTime();
  const { closeSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);
  const { error: formError, isProcessing, submitForm, complete, data } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPromotions : updateEntitlementPlan,
    onComplete: () => {
      closeSidebar();
      refetch();
    },
  });

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const [activeEntitlementPromotionsToRemove, setActiveEntitlementPromotionsToRemove] = useState<SelectedPromotion[]>(
    []
  );
  const [scheduledPromotionsToRemove, setScheduledPromotionsToRemove] = useState<SelectedPromotion[]>([]);
  const [commonSchedulesAndActivePromotions, setCommonSchedulesAndActivePromotions] = useState<SelectedPromotion[]>([]);
  const patchRemovePromotionEnabled = isEnabled(ENABLE_REMOVE_PROMOTION_API);

  const successMessage = () => {
    return (
      <div>
        <p>You can view the changes in the order history tab.</p>
        <Link to={`#order-history`}>View order history</Link>
      </div>
    );
  };

  if (complete && !notificationSent) {
    let notificationOptions;

    if (data?.transitionTime === 'SCHEDULED') {
      notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('remove-promotion-scheduled')
        .withHeader('Changes applied')
        .withMessage('Removed promotion has been scheduled for the next billing cycle.')
        .build();

      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.REMOVE_SCHEDULED,
        actionSubject: AnalyticsTrackSubject.DISCOUNT,
        attributes: {
          entitlementId: entitlement.id,
        },
      });
    } else {
      notificationOptions = successNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withId('remove-promotion-success')
        .withHeader('Changes applied')
        .withMessage(successMessage())
        .build();

      analytics.sendTrackEvent({
        action: AnalyticsTrackAction.REMOVED,
        actionSubject: AnalyticsTrackSubject.DISCOUNT,
        attributes: {
          entitlementId: entitlement.id,
        },
      });
    }

    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const scheduledPromotions = entitlement.scheduledChanges?.promotions as Maybe<Promotion[]>;
  const hasScheduledPromotions: boolean = (scheduledPromotions ?? []).length > 0;
  const entitlementPromotions = entitlement?.promotions ?? ([] as Promotion[]);
  const hasEntitlementPromotions: boolean = entitlementPromotions?.length > 0;
  const promotions = hasScheduledPromotions ? scheduledPromotions : entitlementPromotions;
  const hasNoPromotions: boolean = (promotions ?? []).length === 0;
  const isStatuspageProduct = isStatuspage(entitlement?.productId);
  const { parentSen } = entitlement;
  const hasParentEntitlement = () => {
    if (parentSen) {
      return true;
    } else {
      return false;
    }
  };

  // add all the scheduled promotions for removal when removing an active promotion
  useEffect(() => {
    if (activeEntitlementPromotionsToRemove.length) {
      const allScheduledPromotions = defaultTo(
        scheduledPromotions?.map((promotion, index) => {
          return { promotionId: promotion.id as string, index: index.toString() };
        }),
        []
      );
      setScheduledPromotionsToRemove(allScheduledPromotions);
    }
  }, [activeEntitlementPromotionsToRemove, scheduledPromotions]);

  const updateEntitlementPromotionsToRemove = (promotionId: string, index: string, isRemoved: boolean) => {
    if (isRemoved) {
      setActiveEntitlementPromotionsToRemove([...activeEntitlementPromotionsToRemove, { promotionId, index }]);
    } else {
      const newPromotionsToRemove = activeEntitlementPromotionsToRemove.filter(
        (promo) => promotionId !== promo.promotionId
      );
      setActiveEntitlementPromotionsToRemove(newPromotionsToRemove);
    }
  };

  const checkSamePromotionInActive = (promotionId: string, _: string, isRemoved: boolean) => {
    if (isRemoved) {
      let ind = -1;
      if (
        entitlementPromotions.some((promotion: Maybe<Promotion>, index: number) => {
          ind = index;
          return promotion?.id === promotionId;
        })
      ) {
        setCommonSchedulesAndActivePromotions([
          ...commonSchedulesAndActivePromotions,
          { promotionId, index: `${ind}` },
        ]);
      }
    } else {
      const newPromotionsToRemove = commonSchedulesAndActivePromotions.filter(
        (promo) => promotionId !== promo.promotionId
      );
      setCommonSchedulesAndActivePromotions(newPromotionsToRemove);
    }
  };

  const updateScheduledPromotionsToRemove = (promotionId: string, index: string, isRemoved: boolean) => {
    checkSamePromotionInActive(promotionId, index, isRemoved);
    if (isRemoved) {
      setScheduledPromotionsToRemove([...scheduledPromotionsToRemove, { promotionId, index }]);
    } else {
      const newPromotionsToRemove = scheduledPromotionsToRemove.filter((promo) => promotionId !== promo.promotionId);
      setScheduledPromotionsToRemove(newPromotionsToRemove);
    }
  };

  const handleFormSubmit = (formData: FormData) => {
    logTimeStamp(startTime, 'Remove Promotion', SignalFxFEPerfStore.FE_USER_TASK_REMOVE_PROMO_TIME);

    const requestData = constructRequestData(
      entitlement,
      {
        activeEntitlementPromotionsToRemove,
        scheduledPromotionsToRemove,
        commonSchedulesAndActivePromotions,
      },
      patchRemovePromotionEnabled,
      formData,
      isOmsServiceV2Enabled
    );
    if (isApprovalDeskEnabled) {
      submitRequestAfterApprovalCancelation(() => submitForm(requestData), hasOpenApprovalRequest);
    } else {
      submitForm(requestData);
    }
  };

  const getModifiedActivePromotionList = () => {
    let activePromotions: ActivePromotion[] = defaultTo(entitlementPromotions, []).map((promotion) => ({
      ...promotion,
      isRemoved: false,
    }));
    if (commonSchedulesAndActivePromotions.length > 0) {
      activePromotions = defaultTo(activePromotions, []).map((promotion) => {
        if (commonSchedulesAndActivePromotions.some((promo) => promo.promotionId === promotion?.id)) {
          return { ...promotion, isRemoved: true };
        } else return promotion;
      });

      return activePromotions;
    }

    return activePromotions;
  };

  const hasActiveEntitlementPromotionsToRemove = activeEntitlementPromotionsToRemove.length > 0;
  const isApplyButtonDisabled =
    hasNoPromotions ||
    !(scheduledPromotionsToRemove.length > 0 || activeEntitlementPromotionsToRemove.length > 0) ||
    !!taskListError;

  const isAppAtNoticeWarningShown = React.useMemo(() => {
    return entitlement?.isApp && isOfferingAtNotice(entitlement);
  }, [entitlement]);

  return (
    <div className="entitlement-remove-promotion-form">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps }) => (
          <form {...formProps}>
            <FormHeader title="Remove discount">
              <div className="inline-block">
                {/* @ts-ignore */}
                <HeaderIcon {...(entitlement as EntitlementDetails)} />
              </div>
              <span className="ml-4">
                {hasParentEntitlement() && isStatuspageProduct
                  ? `${entitlement?.edition}`
                  : `${entitlement?.productName ?? 'Unnamed product'}`}{' '}
                {entitlement.entitlementNumber}
              </span>
            </FormHeader>
            {!!taskListError && (
              <FormSection>
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to remove promotion for the following reason"
                      message={taskListError.message}
                    />
                  }
                />
              </FormSection>
            )}
            {!!cancelApprovalRequestError && (
              <FormSection>
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to remove promotion for the following reason"
                      message={cancelApprovalRequestError.message}
                    />
                  }
                />
              </FormSection>
            )}
            {hasOpenApprovalRequest && (
              <FormSection>
                <InfoPanel
                  type="warning"
                  message={
                    <div className="approval-request-inflight-banner">
                      One or more approval requests are currently in progress. If you perform this action, those
                      approval requests will be canceled automatically.
                      {taskListData?.taskList?.tasks?.length && (
                        <TaskDetailAction
                          task={taskListData.taskList.tasks[0]}
                          text="View approval request details"
                          refetch={refetch}
                          refetchAll={refetch}
                        />
                      )}
                    </div>
                  }
                />
              </FormSection>
            )}
            <FormSection>
              <InflightInvoiceBanner
                transactionAccountId={entitlement.transactionAccountId as string}
                message={
                  'An invoice is currently being generated. All orders included in this invoice will be canceled and the subscription will be changed immediately.'
                }
              />
            </FormSection>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel
                      title="We're unable to remove promotion for the following reason"
                      message={formError}
                    />
                  }
                />
              )}
            </FormSection>
            <FormSection>
              {isAppAtNoticeWarningShown && (
                <InfoPanel
                  type="warning"
                  title="This offering is in the process of being archived"
                  message="Keep in mind that any changes will only apply until the subscription end date."
                />
              )}
            </FormSection>
            {hasNoPromotions ? (
              <FormSection>
                <h3>No Promotions or Scheduled Promotions available to remove</h3>
              </FormSection>
            ) : (
              <>
                {hasEntitlementPromotions && (
                  <FormSection>
                    <ActivePromotionSection
                      promotions={getModifiedActivePromotionList()}
                      updatePromotionsToRemove={updateEntitlementPromotionsToRemove}
                    />
                  </FormSection>
                )}
                {hasScheduledPromotions && (
                  <>
                    {hasActiveEntitlementPromotionsToRemove && (
                      <FormSection>
                        <InfoPanel
                          type="warning"
                          message="Removal of any active promotion will also remove all scheduled promotions."
                        />
                      </FormSection>
                    )}
                    <FormSection>
                      <ActivePromotionSection
                        promotions={scheduledPromotions}
                        updatePromotionsToRemove={updateScheduledPromotionsToRemove}
                        heading="Promotions to be applied to next bill"
                        enableIsRemoved={hasActiveEntitlementPromotionsToRemove}
                      />
                    </FormSection>
                  </>
                )}
              </>
            )}
            <FormSection>
              <Field
                name="supportTicketLink"
                label={'Support ticket number'}
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      className="support-ticket"
                      name="supportTicketLink"
                      placeholder={'Add GSAC ticket number'}
                      testId="supportTicketLink-field"
                    />
                    {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormSection>
              <Field
                label={'Internal comment'}
                name="handNote"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield {...fieldProps} name="handNote" testId="hand-note" placeholder={'Add comment'} />
                    {error && <ErrorMessage>This field is required</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="warning"
                  type="submit"
                  isLoading={isProcessing || cancelApprovalRequestProcessing}
                  testId="remove-promotion"
                  isDisabled={isApplyButtonDisabled}
                >
                  Apply
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
          </form>
        )}
      </Form>
    </div>
  );
});
